import { useEffect, useState, useRef, useCallback } from "react";
import { Box, Flex, Spinner, Tooltip } from "@chakra-ui/react";
import { DropDown } from "@reconvert/react-ui-component";
import { getAllPageConditions, getCountries, getCustomerSegment, getLanguage, getOrderTags, getProductSalesChannels, getProductVendors, getProductTypes, getShippingMethods, getTags, getCustomerTags } from "../../../../services/conditions.service";
import { ConditionFact, PageType } from "@reconvert/reconvert-utils";
import { handleFilterCondition } from "../../../../utils/getFilterConditionData";
import { ConditonData } from "../../../../hooks/useAndOrGrouping";
import { PageNames } from "../../../../app/slice/appSlice.model";
import { useTranslation } from "react-i18next";
import { FULFILLMENT_STATUS, PAYMENT_STATUS_OPTIONS, PAYMENT_METHODS_OPTIONS } from "../../../../utils/constant";
import { handleCapitalize } from "../../../../utils/helpers";
import i18n from 'i18next';

interface ProductTagSelectorProps {
  conditionFact: ConditionFact;
  label: string;
  itemsValue: string | string[];
  setItemsValue: (data: { items: string | string[]; info: string | string[] }) => void;
  isOptionCreatable?: boolean;
  conditionData?: ConditonData | undefined
  isValidationAdd?: boolean;
  validationMessage?: string;
  showToolTip?: boolean;
  toolTipText?: string;
  placeholder?: string;
  isCustomerTag?: boolean;
  fieldError?: string;
}

const TooltipWrapper = ({ children, label, showToolTip = false }: { children: React.ReactNode, label: string, showToolTip?: boolean }) => {
  if (showToolTip) {

    return (
      <Tooltip label={label} fontSize="xs" placement="top" hasArrow>
        {children}
      </Tooltip>
    );
  }

  return (
    <>
      {children}
    </>
  );
};

const ProductTagSelector = ({
  itemsValue,
  setItemsValue,
  label,
  conditionFact,
  isOptionCreatable = false,
  conditionData,
  isValidationAdd = false,
  validationMessage = "",
  showToolTip = false,
  toolTipText = "",
  placeholder = i18n.t("Select..."),
  isCustomerTag = false,
  fieldError = ""
}: ProductTagSelectorProps) => {
  const [key, setKey] = useState<string>(conditionFact);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { t } = useTranslation();
  const [placement, setPlacement] = useState<"top" | "bottom">("bottom");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(()=> {
    setKey(new Date().toString());
  },[options])

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setIsLoading(true);
        switch (conditionFact) {
          case ConditionFact.PRODUCT_TAGS:
            try {
              let newTags: string[] = [];
              const items = await getTags();

              if (Array.isArray(itemsValue) && itemsValue.length > 0) {
                newTags = itemsValue?.filter((element: string) => !items?.tags?.includes(element));
              }

              const tagsWithLabel = items?.tags?.map((tag: string) => {
                return { label: tag, value: tag };
              });

              const newTagsWithLabel =
                newTags?.map((tag: string) => {
                  return { label: tag, value: tag };
                }) || [];

              setOptions([...tagsWithLabel, ...newTagsWithLabel]);
            } catch (error) {
              console.error("Failed to fetch tags", error);
            }
            break;

          case ConditionFact.PRODUCT_SALES_CHANNEL:
            try {
              const salesChannels = await getProductSalesChannels({
                limit: 100,
                startCursor: "",
                endCursor: "",
              });

              setOptions(
                salesChannels?.channels?.map((a: any) => {
                  return { label: a.name, value: a.id.split("/").pop() };
                }),
              );
            } catch (error) {
              console.error("Failed to fetch product sales channels:", error);
            }
            break;

          case ConditionFact.CUSTOMER_LANGUAGE:
            try {
              const languageResponse = await getLanguage();

              setOptions(
                languageResponse?.languages?.map((a: any) => {
                  return { label: a.name, value: a.code };
                }),
              );
            } catch (error) {
              console.error("Failed to fetch languages:", error);
            }
            break;

          case ConditionFact.CUSTOMER_COUNTRY:
            try {
              const countriesResponse = await getCountries();

              setOptions(
                countriesResponse?.countries?.map((a: any) => {
                  return { label: a.name, value: a.code };
                }),
              );
            } catch (error) {
              console.error("Failed to fetch countries:", error);
            }
            break;
          case ConditionFact.PREV_PAGE_OFFER:
            try {
              const conditionResponse = await getAllPageConditions();

              if (conditionData) {
                setOptions(
                  conditionResponse?.data?.filter((_: any) => handleFilterCondition(_, conditionData.operator)).map((a: any) => {
                    return { label: ` {${PageNames[a.location as PageType]}} ${a.name} `, value: a.id };
                  }),
                );
              }
            } catch (error) {
              console.error("Failed to fetch countries:", error);
            }
            break;

          case ConditionFact.CUSTOMER_SEGMENTS:
            try {
              const customerSegementResponse = await getCustomerSegment();

              setOptions(
                customerSegementResponse?.map((response: any) => {
                  return { label: response.name, value: response.id };
                }),
              );
            } catch (error) {
              console.error("Failed to fetch Customer Segments", error);
            }
            break;
          
          case ConditionFact.ORDER_TAGS:
            try {
              let newTags: string[] = [];
              const items = await getOrderTags();

              if (Array.isArray(itemsValue) && itemsValue.length > 0) {
                newTags = itemsValue?.filter((element: string) => !items?.orderTags?.includes(element));
              }

              const tagsWithLabel = items?.orderTags?.map((tag: string) => {
                return { label: tag, value: tag };
              });

              const newTagsWithLabel =
                newTags?.map((tag: string) => {
                  return { label: tag, value: tag };
                }) || [];

              setOptions([...tagsWithLabel, ...newTagsWithLabel]);
            } catch (error) {
              console.error("Failed to fetch tags", error);
            }
            break;
  

          case ConditionFact.PRODUCT_TYPE:
            try {
              const productTypesResponse = await getProductTypes();

              if (productTypesResponse?.productTypes?.length) {
                setOptions(productTypesResponse?.productTypes?.map((response: string) => {
                  return { label: response, value: response };
                }));
              }
            } catch (error) {
              console.error("Failed to fetch Product Types", error);
            }

            break;
          
          case ConditionFact.FULFILLMENT_STATUS:
            try {
              const fulfillmentStatusResponse: { name: string; id: string }[] = await new Promise((resolve, reject) => {
                  resolve(FULFILLMENT_STATUS);
              });

              setOptions(
                fulfillmentStatusResponse.map((response: { name: string; id: string }) => {
                  return { label: response.name, value: response.id };
                }),
              );
              setKey(Date.now().toString());
            } catch (error) {
              console.error("Failed to fetch Fulfillment Status", error);
            }
            break;

          case ConditionFact.PAYMENT_STATUS:
            try {
              const paymentStatusResponse = await new Promise((resolve) => {
                  resolve(PAYMENT_STATUS_OPTIONS);
              });
              
              setOptions(paymentStatusResponse as { label: string; value: string }[]);
              setKey(Date.now().toString());
            } catch (error) { 
              console.error("Failed to fetch payment statuses:", error);
            }
          break;
          
          case ConditionFact.PRODUCT_VENDOR:
            try {
              const productVendorResponse = await getProductVendors({
                limit: 100,
                startCursor: "",
                endCursor: "",
              });

              setOptions(productVendorResponse.vendors?.map((response: string) => {
                return { label: response, value: response };
              }));
            } catch (error) {
              console.error("Failed to fetch Product Vendors", error);
            }
            break;
            
          case ConditionFact.SHIPPING_METHOD:
            try {
              const shippingMethodResponse = await getShippingMethods();

              setOptions(
                shippingMethodResponse?.shippingMethods?.map((response: any) => {
                  return { label: handleCapitalize(response.name), value: response.name };
                }),
              );
            } catch (error) {
              console.error("Failed to fetch Shipping Methods", error);
            }
            break;

          case ConditionFact.PRODUCT_IS_AVAILABLE_AT_SALES_CHANNEL:
            try {
              const salesChannels = await getProductSalesChannels({
                limit: 100,
                startCursor: "",
                endCursor: "",
              });

              setOptions(
                salesChannels?.channels?.map((a: any) => {
                  return { label: a.name, value: a.name };
                }),
              );
            } catch (error) {
              console.error("Failed to fetch product sales channels:", error);
            }
            break;

          case ConditionFact.PAYMENT_METHOD:
            try {
              const paymentMethodsResponse: { displayName: string; value: string }[] = await new Promise((resolve, reject) => {
                resolve(PAYMENT_METHODS_OPTIONS);
              });

              setOptions(
                paymentMethodsResponse.map((response: { displayName: string; value: string }) => {
                  return { label: response.displayName, value: response.value };
                }),
              );
              setKey(new Date().toString());
            } catch (error) {
              console.error("Failed to fetch payment methods", error);
            }
            break;

          case ConditionFact.CUSTOMER_TAGS:
            try {
              let newTags: string[] = [];
              const items = await getCustomerTags();

              if (Array.isArray(itemsValue) && itemsValue.length > 0) {
                newTags = itemsValue?.filter((element: string) => !items?.customerTags?.includes(element));
              }

              const tagsWithLabel = items?.customerTags?.map((tag: string) => {
                return { label: tag, value: tag };
              });

              const newTagsWithLabel =
                newTags?.map((tag: string) => {
                  return { label: tag, value: tag };
                }) || [];

              setOptions([...tagsWithLabel, ...newTagsWithLabel]);
            } catch (error) {
              console.error("Failed to fetch customer tags", error);
            }

            break;

          default:
            console.error("Unknown conditionFact:", conditionFact);

            break;

            case ConditionFact.CUSTOMER_TAG:
              try {
                let newTags: string[] = [];
                const items = ["Tag 1", "Tag 2", "Tag 3", "Tag 4", "Tag 5"];

                if (Array.isArray(itemsValue) && itemsValue.length > 0) {
                  newTags = itemsValue?.filter((element: string) => !items?.includes(element));
                }
  
                const tagsWithLabel = items?.map((tag: string) => {
                  return { label: tag, value: tag };
                });
  
                const newTagsWithLabel =
                  newTags?.map((tag: string) => {
                    return { label: tag, value: tag };
                  }) || [];
  
                setOptions([...tagsWithLabel, ...newTagsWithLabel]);
              } catch (error) {
                console.error("Failed to fetch tags", error);
              }
              break;
        }
      } catch (error) {
        console.error("Failed to fetch options:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOptions();
    setKey(conditionFact);
  }, [conditionFact, conditionData && conditionData.operator]);

  useEffect(() => {
    if (isValidationAdd) {
      if (options.length === 0 && !isLoading) {
        setErrorMessage(validationMessage);
      } else {
        setErrorMessage("");
      }
    } else {
      setErrorMessage("");
    }
  }, [isValidationAdd, options, itemsValue, isLoading]);

  const handleClick = useCallback(() => {
    if (containerRef.current) {
      // adjust the placement dropdown popup based on the space below and options length
      const rect = containerRef.current.getBoundingClientRect();

      const spaceBelow = window.innerHeight - rect.bottom;

      const itemHeight = isOptionCreatable ? 50 : 40;
      const maxVisibleItems = isOptionCreatable ? 6 : 8;
      const padding = 16;
      const visibleItems = Math.min(options.length, maxVisibleItems);
      const requiredHeight = visibleItems * itemHeight + padding;

      setPlacement(spaceBelow < requiredHeight ? "top" : "bottom");
    }
  }, [containerRef, options.length]);

  return (
    <TooltipWrapper label={toolTipText} showToolTip={showToolTip}>
    <Box mt={2} ref={containerRef} onMouseDown={handleClick}>
      {!isLoading ? (
        <>
          <DropDown
            key={key}
            isMulti
            label={label}
            name={label}
            labelWithBackground
            onValueChange={val => {
              setItemsValue({ items: val, info: options.filter(a => val.includes(a.value)).map(a => a.label) });
            }}
            options={options || []}
            showLabel
            showCheckBox
            size="sm"
            tagView
            value={itemsValue}
            autowrap
            isCreatable={isOptionCreatable}
            onCreateOption={(val: string) => {
              let processedVal = val;

              if (isCustomerTag) {
                processedVal = val.trim().toLowerCase();
              }

              setOptions([...options, { label: processedVal, value: processedVal }]);
              setItemsValue({ items: [...itemsValue, processedVal], info: [...options.filter(a => itemsValue.includes(a.value)).map(a => a.label), processedVal] });
              setKey(new Date().toString());
            }}
            isInvalid={(isValidationAdd && !!errorMessage) || !!fieldError}
            error={t(errorMessage) || fieldError}
            isRequired={isValidationAdd}
            placeholder={placeholder}
            placement={placement}
          />
        </>
      ) : (
        <Flex justifyContent={"center"} mt="12px">
          <Spinner />
        </Flex>
      )}
    </Box>
  </TooltipWrapper>
  );
};

export default ProductTagSelector;
